import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DateRangePicker from './DateRangePicker';
import { getDashboardData } from '../api/dashboard';
import { getLinkConversions } from '../api/shopifyConversions';
import SharedChannelIcon from './ChannelIcon.jsx';
import '../styles/pages/dashboard.css';

// Heavy charting libraries are code-split so they load only when the Dashboard
// (and, for the map, the expanded geo section) is actually viewed — keeping
// recharts and react-simple-maps out of the app's initial bundle.
const DashboardVolumeChart = lazy(() => import('./DashboardVolumeChart'));
const DashboardGeoMap = lazy(() => import('./DashboardGeoMap'));

// Format a monetary amount in the given currency (falls back to a bare number).
const fmtMoney = (amount, currency) => {
  if (currency == null) return `${amount}`;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

const KNOWN_CHANNELS = new Set(['whatsapp', 'facebook', 'messenger', 'instagram', 'telegram', 'email', 'webchat', 'tiktok', 'tiktok_shop']);

// The backend already collapses duplicates to canonical ISO 639 codes, but the
// detector can still emit the odd non-standard token. This override map maps
// those to a proper ISO code; everything else is resolved to a localized full
// name via Intl.DisplayNames inside the component (see `languageName`).
const LANGUAGE_OVERRIDES = {
  bangla: 'bn',
};

const ChannelIcon = ({ channel }) => {
  const key = KNOWN_CHANNELS.has(String(channel || '').toLowerCase()) ? channel : 'webchat';
  return <span className="activity-channel"><SharedChannelIcon channel={key} size={16} /></span>;
};

// Skeleton components
const SkeletonBlock = ({ width = '100%', height = '20px', style }) => (
  <div className="skeleton-pulse" style={{ width, height, borderRadius: '6px', ...style }} />
);

const SkeletonMetricCard = () => (
  <div className="metric-card">
    <SkeletonBlock width="60px" height="32px" />
    <SkeletonBlock width="120px" height="14px" style={{ marginTop: '8px' }} />
    <SkeletonBlock width="90px" height="12px" style={{ marginTop: '6px' }} />
  </div>
);

const SkeletonRow = () => (
  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '10px 0' }}>
    <SkeletonBlock width="20px" height="20px" />
    <SkeletonBlock width="100px" height="14px" />
    <SkeletonBlock height="14px" style={{ flex: 1 }} />
    <SkeletonBlock width="80px" height="14px" />
    <SkeletonBlock width="40px" height="14px" />
  </div>
);

const Dashboard = ({ showRevenueCard = false }) => {
  const { t, i18n } = useTranslation('dashboard');

  // Resolve a stored language value to its full name in the admin's UI language
  // (e.g. "en" -> "English" / "Inglés", "bn" -> "Bengali"). Falls back to the
  // raw value only for a genuinely unknown, non-ISO token.
  const langDisplay = useMemo(
    () => new Intl.DisplayNames([i18n.language || 'en'], { type: 'language' }),
    [i18n.language],
  );
  const languageName = useCallback((code) => {
    if (!code) return code;
    const normalized = String(code).trim().toLowerCase();
    const key = LANGUAGE_OVERRIDES[normalized] || normalized;
    try {
      const name = langDisplay.of(key);
      return name && name !== key ? name : code;
    } catch {
      return code;
    }
  }, [langDisplay]);
  const navigate = useNavigate();
  // Date range state — default to last 7 days
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6); d.setHours(0,0,0,0); return d;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date(); d.setHours(23,59,59,999); return d;
  });

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Collapse state for geographic map
  const [isGeoExpanded, setIsGeoExpanded] = useState(true);

  // Attributed "Revenue from AI" — pulled from the same source as the
  // Conversions page (getLinkConversions). Only fetched when the merchant is
  // entitled to it AND has Shopify connected (showRevenueCard, gated in
  // AdminApp exactly like the Conversions page). It reflects the conversion
  // endpoint's fixed recent window, so it is intentionally NOT re-filtered by
  // the dashboard's date-range picker above.
  const [revData, setRevData] = useState(null);
  useEffect(() => {
    if (!showRevenueCard) return;
    let alive = true;
    getLinkConversions()
      .then((d) => { if (alive) setRevData(d); })
      .catch(() => { if (alive) setRevData(null); });
    return () => { alive = false; };
  }, [showRevenueCard]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getDashboardData(startDate, endDate);
      setData(result);
      // Auto-expand geo if 3+ locations
      if (result.geographicData && result.geographicData.length >= 3) {
        setIsGeoExpanded(true);
      }
    } catch (err) {
      console.error('Dashboard fetch failed:', err);
      setError(err.message || t('loadError', 'Failed to load dashboard data'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Derived values
  const heroMetrics = data?.heroMetrics;
  const volumeData = data?.volumeData || [];
  const geographicData = data?.geographicData || [];
  const recentConversations = data?.recentConversations || [];
  const topTopics = data?.topTopics || [];
  const escalatedTopics = data?.escalatedTopics || [];
  const channelData = data?.channelData || [];
  const languageData = data?.languageData || [];

  const totalGeoConversations = geographicData.reduce((acc, d) => acc + d.count, 0);
  const uniqueCountries = new Set(geographicData.map(d => d.country)).size;

  return (
    <div id="page-dashboard">
      {/* Header with date picker */}
      <div className="dashboard-header">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateChange}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="dashboard-error">
          <span>{error}</span>
          <button onClick={fetchData} className="retry-btn">{t('retry', 'Retry')}</button>
        </div>
      )}

      {/* Hero Metrics — 3 cards, +1 "Revenue from AI" card when entitled + Shopify-connected */}
      <div className={`hero-metrics${showRevenueCard ? ' hero-metrics--four' : ''}`}>
        {loading && !data ? (
          <>
            <SkeletonMetricCard />
            <SkeletonMetricCard />
            <SkeletonMetricCard />
            {showRevenueCard && <SkeletonMetricCard />}
          </>
        ) : heroMetrics ? (
          <>
            <div className="metric-card">
              <div className="metric-value">{heroMetrics.conversationsHandled.value}</div>
              <div className="metric-label">{t('metrics.conversationsHandled', 'Conversations Handled')}</div>
              <div className="metric-change">
                {heroMetrics.conversationsHandled.change != null && (
                  <>{heroMetrics.conversationsHandled.change > 0 ? '↑' : '↓'} {Math.abs(heroMetrics.conversationsHandled.change)}% </>
                )}
                {heroMetrics.conversationsHandled.period}
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-value">{heroMetrics.resolutionRate.value}%</div>
              <div className="metric-label">{t('metrics.resolvedByAi', 'Resolved by AI')}</div>
              <div className="metric-change">
                {heroMetrics.resolutionRate.change != null && (
                  <>{heroMetrics.resolutionRate.change > 0 ? '↑' : '↓'} {Math.abs(heroMetrics.resolutionRate.change)}% </>
                )}
                {heroMetrics.resolutionRate.period}
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-value">{heroMetrics.timeSaved.hours}h {heroMetrics.timeSaved.minutes}m</div>
              <div className="metric-label">{t('metrics.timeSaved', 'Time Saved')}</div>
              <div className="metric-subtext">{t('metrics.timeSavedSubtext', 'Based on avg handling time')}</div>
            </div>

            {showRevenueCard && (revData ? (
              <div className="metric-card">
                <div className="metric-value">
                  {(revData.revenue?.length
                    ? revData.revenue.map((r) => fmtMoney(r.amount, r.currency)).join(' · ')
                    : fmtMoney(0, revData.revenue?.[0]?.currency))}
                </div>
                <div className="metric-label">{t('metrics.revenueFromAi', 'Revenue from AI')}</div>
                <div
                  className="metric-link"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/link-conversions')}
                >
                  {t('metrics.ordersLine', { n: revData.total_orders || 0, defaultValue: '{{n}} orders' })}
                  {' · '}
                  {t('metrics.viewConversions', 'View conversions →')}
                </div>
              </div>
            ) : (
              <SkeletonMetricCard />
            )
            )}
          </>
        ) : null}
      </div>

      {/* Conversation Volume Chart */}
      <div className="chart-card">
        <div className="section-header">
          <h2 className="section-title">{t('conversationVolume.title')}</h2>
          <div className="chart-legend">
            <span className="legend-item legend-item--ai">{t('conversationVolume.aiHandled')}</span>
            <span className="legend-item legend-item--human">{t('conversationVolume.humanHandled')}</span>
          </div>
        </div>
        {loading && !data ? (
          <SkeletonBlock height="300px" />
        ) : (
          <Suspense fallback={<SkeletonBlock height="300px" />}>
            <DashboardVolumeChart volumeData={volumeData} />
          </Suspense>
        )}
      </div>

      {/* Geographic Distribution (Collapsible) */}
      <div className="chart-card">
        <div className="section-header" style={{ cursor: 'pointer' }} onClick={() => setIsGeoExpanded(!isGeoExpanded)}>
          <h2 className="section-title">{t('geographic.title')}</h2>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
            <span className="map-stat">{totalGeoConversations} {t('geographic.conversations')}</span>
            <span className="map-stat">{uniqueCountries} {t('geographic.countries')}</span>
            <span className={`geo-chevron${!isGeoExpanded ? ' geo-chevron--collapsed' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6L8 10L12 6" />
              </svg>
            </span>
          </div>
        </div>

        {isGeoExpanded && (
          <>
            {loading && !data ? (
              <SkeletonBlock height="400px" />
            ) : (
              <>
                <div className="map-container">
                  <Suspense fallback={<SkeletonBlock height="400px" />}>
                    <DashboardGeoMap geographicData={geographicData} />
                  </Suspense>
                </div>
                <div className="map-legend">
                  <span className="map-legend-item">
                    <span className="map-dot" style={{ background: '#10b981' }}></span>
                    {t('geographic.legendHigh')}
                  </span>
                  <span className="map-legend-item">
                    <span className="map-dot" style={{ background: '#f59e0b' }}></span>
                    {t('geographic.legendMedium')}
                  </span>
                  <span className="map-legend-item">
                    <span className="map-dot" style={{ background: '#ef4444' }}></span>
                    {t('geographic.legendLow')}
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Section: Recent Conversations & Top Topics */}
      <div className="dashboard-section">
        {/* Recent Conversations */}
        <div className="section-card">
          <div className="section-header">
            <h2 className="section-title">{t('recentConversations.title')}</h2>
            <a href="#" className="view-all-link" onClick={(e) => { e.preventDefault(); navigate('/conversations'); }}>{t('recentConversations.viewAll')}</a>
          </div>
          <div className="activity-list">
            {loading && !data ? (
              <>
                <SkeletonRow /><SkeletonRow /><SkeletonRow />
                <SkeletonRow /><SkeletonRow /><SkeletonRow />
              </>
            ) : recentConversations.length > 0 ? (
              recentConversations.map(conv => (
                <div key={conv.id} className="activity-item">
                  <ChannelIcon channel={conv.channel} />
                  <span className="activity-customer">{conv.customer}</span>
                  <span className="activity-question">{conv.question}</span>
                  <span className={`activity-status status-${conv.status}`}>
                    {conv.status === 'ai' ? t('recentConversations.resolvedByAi') : t('recentConversations.handedToHuman')}
                  </span>
                  <span className="activity-time">{conv.time}</span>
                </div>
              ))
            ) : (
              <div className="empty-state">{t('recentConversations.empty', 'No recent conversations')}</div>
            )}
          </div>
        </div>

        {/* Top Topics */}
        <div className="section-card">
          <div className="section-header">
            <h2 className="section-title">{t('topTopics.title')}</h2>
          </div>
          {loading && !data ? (
            <div className="topics-list">
              <SkeletonBlock height="24px" style={{ marginBottom: '8px' }} />
              <SkeletonBlock width="85%" height="24px" style={{ marginBottom: '8px' }} />
              <SkeletonBlock width="70%" height="24px" style={{ marginBottom: '8px' }} />
              <SkeletonBlock width="55%" height="24px" style={{ marginBottom: '8px' }} />
              <SkeletonBlock width="40%" height="24px" />
            </div>
          ) : (
            <>
              <div className="topics-list">
                {topTopics.length > 0 ? topTopics.map((topic, idx) => (
                  <div key={idx} className="topic-item" style={{ '--width': `${100 - idx * 16}%` }}>
                    <span className="topic-rank">{idx + 1}.</span>
                    <span className="topic-text">"{topic.topic}"</span>
                    <span className="topic-count">({topic.count} {t('topTopics.times')})</span>
                  </div>
                )) : (
                  <div className="empty-state">{t('topTopics.empty', 'No topics data available')}</div>
                )}
              </div>

              <div className="section-header topics-divider">
                <h2 className="section-title">{t('topTopics.escalatedTitle', 'Topics Escalated to Human')}</h2>
              </div>
              <div className="topics-list">
                {escalatedTopics.length > 0 ? escalatedTopics.map((topic, idx) => (
                  <div key={idx} className="topic-item escalated" style={{ '--width': `${100 - idx * 25}%` }}>
                    <span className="topic-rank">{idx + 1}.</span>
                    <span className="topic-text">"{topic.topic}"</span>
                    <span className="topic-count">({topic.count} {t('topTopics.times')})</span>
                  </div>
                )) : (
                  <div className="empty-state">{t('topTopics.escalatedEmpty', 'No escalated topics')}</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Section: Channels & Languages */}
      <div className="dashboard-section">
        <div className="section-card">
          <div className="section-header">
            <h2 className="section-title">{t('byChannel.title')}</h2>
          </div>
          {loading && !data ? (
            <div className="channel-breakdown">
              {[1,2,3,4].map(i => (
                <div key={i} className="channel-row">
                  <SkeletonBlock width="80px" height="14px" />
                  <div className="channel-bar-container"><SkeletonBlock height="14px" /></div>
                  <SkeletonBlock width="30px" height="14px" />
                </div>
              ))}
            </div>
          ) : (
            <div className="channel-breakdown">
              {channelData.map((channel, idx) => (
                <div key={idx} className="channel-row">
                  <span className="channel-name">{channel.channel}</span>
                  <div className="channel-bar-container">
                    <div className="channel-bar" style={{ width: `${channel.percentage}%` }} />
                  </div>
                  <span className="channel-count">{channel.count}</span>
                </div>
              ))}
              {channelData.length === 0 && <div className="empty-state">{t('byChannel.empty', 'No channel data')}</div>}
            </div>
          )}
        </div>

        <div className="section-card">
          <div className="section-header">
            <h2 className="section-title">{t('languagesUsed.title')}</h2>
          </div>
          {loading && !data ? (
            <div className="language-breakdown">
              {[1,2,3,4].map(i => (
                <div key={i} className="language-row" style={{ '--width': '100%' }}>
                  <SkeletonBlock width="24px" height="14px" />
                  <SkeletonBlock width="80px" height="14px" />
                  <SkeletonBlock width="60px" height="14px" />
                </div>
              ))}
            </div>
          ) : (
            <div className="language-breakdown">
              {languageData.map((lang, idx) => (
                <div key={idx} className="language-row" style={{ '--width': `${lang.percentage}%` }}>
                  <span className="language-name">{languageName(lang.language)}</span>
                  <span className="language-count">{lang.count} ({lang.percentage}%)</span>
                </div>
              ))}
              {languageData.length === 0 && <div className="empty-state">{t('languagesUsed.empty', 'No language data')}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
